import { useRef, useCallback, useEffect } from "react";

const scheduleDispatch = (
  dispatches: Set<ReturnType<typeof setTimeout>>,
  onDispatch: () => void,
  delay: number
): { cancel: () => void } => {
  const timeout = setTimeout(() => {
    onDispatch();
    dispatches.delete(timeout);
  }, delay);
  dispatches.add(timeout);
  return {
    cancel: () => {
      clearTimeout(timeout);
      dispatches.delete(timeout);
    },
  };
};

/**
 * Returns a function which, when called, will set a timeout that invokes the given callback
 * after the provided number of milliseconds. This is reactive, so the most up-to-date version
 * of `callback` that has been passed into this function is what will be invoked by the timeout
 * when it executes. The return of value this function is an object which has a cancel property,
 * which is a function that when called will cancel the delayed callback if pending.
 *
 * Any timeouts that have not executed before the component is unmounted will automatically
 * be canceled when the component unmounts.
 */
export const useDelayedCallback = (
  callback: () => void
): ((delay: number) => { cancel: () => void }) => {
  const callbackByRef = useRef(callback);
  useEffect(() => {
    callbackByRef.current = callback;
  }, [callbackByRef, callback]);
  const onDispatch = useCallback(() => {
    callbackByRef.current?.();
  }, [callbackByRef]);

  const dispatches = useRef(new Set<ReturnType<typeof setTimeout>>());
  const addDispatch = useCallback(
    (delay: number) => scheduleDispatch(dispatches.current, onDispatch, delay),
    [dispatches, onDispatch]
  );

  useEffect(() => {
    const dispatchesToCleanup = dispatches.current;
    return () =>
      dispatchesToCleanup.forEach((timeout) => clearTimeout(timeout));
  }, [dispatches]);

  return addDispatch;
};
